import './styles/ArticleDetail.scss';

import { useMutation, useQuery } from '@apollo/client';
import { GetApp, Info, Today } from '@mui/icons-material';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { Button, Divider, Grid, Icon, Paper, Skeleton, Typography } from '@mui/material';
import { ShibUser } from '@thro-types/users';

import { isAfter } from 'date-fns';
import parse from 'html-react-parser';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import DELETE_MUTATION from 'gql/mutations/deleteArticle.gql';
import NOTIFY_MUTATION from 'gql/mutations/sendNotificationForArticle.gql';
import ALLOWED_TO_POST from 'gql/queries/PermissionProvider/testAllowedToPost.gql';
import QUERY from 'gql/queries/getArticle.gql';

import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DenseCalendar } from 'app/components/DenseCalendar';
import DetailPage from 'app/components/DetailPage';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import ArticleTypeLogo from 'app/components/RichList/items/ArticleItem/components/ArticleTypeLogo';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { isOrganizer } from 'app/containers/Events/helper';
import { translations } from 'locales/i18n';
import { AuthorRole } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import Article from '../Article';
import { ArticleInspection } from './components/ArticleInspection';

interface Props {}

export const ArticleDetail: FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sendMailsDialogOpen, setSendMailsDialogOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [inspectModalOpen, setInspectModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteArticle] = useMutation(DELETE_MUTATION);
  const [notifyUsers] = useMutation(NOTIFY_MUTATION);
  const allowedToPostQuery = useQuery<any, any>(ALLOWED_TO_POST);

  usePageHeading({
    heading: t(translations.pages.articles.title()),
    desktopOnly: true,
    headingProps: {
      back: location.state?.back ?? '/articles'
    }
  });

  const { user: currentUser, authorRoles } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);

  const articleQuery = useQuery<any, any>(QUERY, {
    variables: { id: parseInt(id!, 10) }
  });

  if (articleQuery.loading) return <Skeleton height={380} variant="rounded" />;
  if (
    isAfter(new Date(articleQuery.data?.article.startDate), new Date()) &&
    !canEdit(articleQuery.data.article, authorRoles, currentUser)
  ) {
    navigate('/articles', { replace: true });
  }

  const handleDelete = async article => {
    if (deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteArticle({
        variables: {
          id: article.id
        }
      });
      dispatch(AlertActions.success(t(translations.pages.articleDetail.deleteSuccessful())));
      navigate('/articles', { replace: true });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.articleDetail.deleteFailed())));
    }
    setDeleting(false);
  };

  const handleNotify = async () => {
    try {
      await notifyUsers({ variables: { articleId: id } });
      dispatch(AlertActions.success(t(translations.pages.articleDetail.notificationSuccessful())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.articleDetail.notificationFailed())));
    }
  };

  const cancelArticleCreation = (reason?) => {
    if (reason && reason === 'backdropClick') return;
    if (disabledCancel) {
      return;
    }
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
  };

  const handleBoldStyles = (text: string) => {
    const texts = text.split('*');
    let bold = false;
    return texts.map(tex => {
      const response = bold ? <b>{tex}</b> : <>{tex}</>;
      bold = !bold;
      return response;
    });
  };

  const editHandlers = article => {
    return (
      <Grid container justifyContent="flex-end">
        <Button
          color="primary"
          variant="contained"
          disabled={deleting}
          onClick={() => setCreateModalOpen(true)}
          style={{ color: 'white', marginBottom: '1em' }}
        >
          {t(translations.common.edit())}
        </Button>
        <span
          style={{
            position: 'relative',
            marginBottom: '1em',
            marginLeft: '1em'
          }}
        >
          <Button
            color="primary"
            variant="contained"
            disabled={deleting}
            onClick={() => setCancelModalOpen(true)}
            style={{
              color: 'white',
              backgroundColor: !deleting ? 'red' : undefined
            }}
          >
            {t(translations.common.delete())}
          </Button>
          {deleting && <SubmittingLoader />}
        </span>
        <ErrorBoundary boundaryTag="articleDetailBoundary">
          <Modal
            open={createModalOpen}
            onClose={cancelArticleCreation}
            headline={t(translations.pages.articles.edit.title())}
            onCancel={() => {
              cancelArticleCreation();
            }}
            disabledCancel={disabledCancel}
          >
            <Article
              onCancel={() => cancelArticleCreation()}
              closeHandler={() => cancelArticleCreation()}
              article={article}
              setDisabledCancel={setDisabledCancel}
            />
          </Modal>
          <Modal
            open={inspectModalOpen}
            onClose={reason => {
              if (reason && reason === 'backdropClick') return;
              setInspectModalOpen(false);
            }}
            headline={t(translations.pages.articles.inspect.title())}
            onCancel={() => {
              setInspectModalOpen(false);
            }}
          >
            <ArticleInspection recipients={article.recipients} />
          </Modal>
          <ConfirmationDialog
            open={cancelModalOpen}
            title={t(translations.pages.articleDetail.deleteConfirm.title())}
            onClose={() => {
              setCancelModalOpen(false);
            }}
            onConfirm={() => {
              setCancelModalOpen(false);
              handleDelete(article);
            }}
          >
            <div>{t(translations.pages.articleDetail.deleteConfirm.message())}</div>
          </ConfirmationDialog>
        </ErrorBoundary>
      </Grid>
    );
  };

  const sideInfo = article => {
    const { appointment } = article;
    if (!appointment) {
      return null;
    }

    return (
      <>
        <Grid item className="article-detail__appointment">
          <Grid container className="article-detail__appointment__heading">
            {t(translations.common.event())}
          </Grid>
          <Grid container className="article-detail__appointment__calendar">
            <Grid container component={Paper} elevation={4}>
              <DenseCalendar
                startDate={appointment.startDate}
                endDate={appointment.endDate}
                markDates
              />
            </Grid>
          </Grid>
          {article?.event_id && (
            <Grid container className="article-detail__appointment__date">
              <Link to={`/events/${article.event_id}`}>
                <Button
                  size="small"
                  className="article-detail__content__aside__inspect"
                  startIcon={<Today />}
                  style={{ margin: 0 }}
                >
                  <Typography>{t(translations.pages.articleDetail.goToEvent())}</Typography>
                </Button>
              </Link>
            </Grid>
          )}
          <Divider variant="middle" className="divider" />
          <Grid container className="article-detail__appointment__date">
            <Grid container className="article-detail__appointment__date__heading">
              <Icon className="far fa-clock" style={{ width: 'auto' }} />
              {t(translations.common.start())}
            </Grid>
            {new Date(appointment.startDate).toLocaleString()}
          </Grid>
          <Grid container className="article-detail__appointment__date">
            <Grid container className="article-detail__appointment__date__heading">
              <Icon className="fas fa-clock" style={{ width: 'auto' }} />
              {t(translations.common.end())}
            </Grid>
            {new Date(appointment.endDate).toLocaleString()}
          </Grid>
          {appointment.venue && (
            <Grid container className="article-detail__appointment__date">
              <Grid container className="article-detail__appointment__date__heading">
                <Icon className="fas fa-map-pin" style={{ width: 'auto' }} />
                {t(translations.pages.articles.create.lifetime.venue())}
              </Grid>
              {appointment.venue}
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  function canEdit(article, authorRoles: AuthorRole[], currentUser: ShibUser) {
    return (
      aclHelper.isSuperUser() ||
      isOrganizer(currentUser, authorRoles, article.organizers) ||
      currentUser.uid.toString() === article.author.id.toString()
    );
  }

  return (
    <>
      <GQLGuard
        query={[articleQuery, allowedToPostQuery]}
        onError={error => {
          dispatch(AlertActions.error(`${t(translations.pages.articleDetail.errorLoading())}`));
          navigate('/articles', { replace: true });
          return null;
        }}
      >
        {([articleData, allowedToPostData]) => {
          const { article } = articleData;
          //! We have to verify when alowedToPost is required
          const canUserEdit = canEdit(article, authorRoles, currentUser);

          return (
            <>
              {canUserEdit && <>{editHandlers(article)}</>}
              <DetailPage sideInfo={sideInfo(article)} className="article-detail">
                <Grid container className="article-detail__content">
                  <Grid item xs={12} md={10} className="article-detail__content__main">
                    <Grid container className="article-detail__content__main__headline">
                      {article.headline}
                    </Grid>
                    <Grid container className="article-detail__content__main__author">
                      {t(translations.pages.articleDetail.articleBy(), {
                        author: `${article.author.firstName} ${article.author.lastName}`,
                        date: new Date(article.startDate).toLocaleDateString(),
                        department: article.department ? ` | ${article.department.title}` : ''
                      })}
                    </Grid>
                    <Grid container className="article-detail__content__main__short-description">
                      {article.event_id ? (
                        <Link to={`/events/${article.event_id}`}>{article.shortDescription}</Link>
                      ) : (
                        <>{article.shortDescription}</>
                      )}
                    </Grid>
                    <Grid
                      container
                      className="article-detail__content__main__article-text ql-editor"
                    >
                      {parse(article.articleText)}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md className="article-detail__content__aside">
                    <Grid container className="article-detail__content__aside__icon">
                      <ArticleTypeLogo articleType={article.type} />
                    </Grid>
                    <Grid container className="article-detail__content__aside__type">
                      {t(translations.pages.articles.type[article.type]())}
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      className="article-detail__content__aside__watchlist"
                    >
                      <WatchlistAddButton
                        categoryName={'ARTICLE'}
                        variant={'contained'}
                        title={article.headline}
                        description={article.shortDescription}
                        date={article.startDate}
                      />
                    </Grid>
                    {article.attachement && (
                      <Grid container justifyContent="center">
                        <Button
                          size="small"
                          className="article-detail__content__aside__download"
                          startIcon={<GetApp />}
                          onClick={() =>
                            handleDownload(
                              article.attachement.split('__', 2)[1],
                              `${import.meta.env.VITE_GATEWAY_URL}/articles/v1/articles/${
                                article.id
                              }/attachement`
                            )
                          }
                        >
                          {t(translations.pages.jobOffers.downloadAttachement())}
                        </Button>
                      </Grid>
                    )}
                    {canUserEdit && (
                      <Grid container justifyContent={'center'}>
                        <Button
                          size="small"
                          className="article-detail__content__aside__inspect"
                          startIcon={<ReportGmailerrorredIcon />}
                          onClick={() => setSendMailsDialogOpen(true)}
                        >
                          {t(translations.pages.articleDetail.sendEmail())}
                        </Button>
                      </Grid>
                    )}
                    {aclHelper.isSuperUser() && (
                      <Grid container justifyContent="center">
                        <Button
                          size="small"
                          className="article-detail__content__aside__inspect"
                          startIcon={<Info />}
                          onClick={() => setInspectModalOpen(true)}
                        >
                          {t(translations.pages.jobOffers.inspectArticle())}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </DetailPage>
            </>
          );
        }}
      </GQLGuard>
      <ConfirmationDialog
        open={sendMailsDialogOpen}
        title={t(translations.pages.articleDetail.confirmSendEmail())}
        onClose={() => setSendMailsDialogOpen(false)}
        onConfirm={() => {
          handleNotify();
          setSendMailsDialogOpen(false);
        }}
      >
        {handleBoldStyles(t(translations.pages.articleDetail.confirmSendEmailDetail()))}
      </ConfirmationDialog>
    </>
  );
};
